import React, { useState } from 'react'

import { Visibility, VisibilityOff } from '@mui/icons-material'
import { Box, Container, Divider, IconButton, Typography } from '@mui/material'
import * as Yup from 'yup'

import Form from '../../shared/components/Form/Form.component'
import Link from '../../shared/components/Link/Link.component'

import { login } from '../../redux/states/auth/login/login.slice'
import { LoginRequest } from '../../redux/states/auth/login/login.types'
import { FlightItinerary } from '../../redux/states/flights/getAllFlightItineraries/getAllFlightItineraries.types'
import { FareOffer } from '../../redux/states/flights/getFlightFares/types/FareDetailsResponse.types'
import { OfferRequest } from '../../redux/states/offers/getOfferRequests/getOfferRequests.types'
import router from '../../router/functions/router.functions'
import routes from '../../router/routes.dictionary'
import { FormField } from '../../shared/components/Form/Form.types'
import { TripPreferences } from '../../shared/components/TripPreferencesModal/TripPreferencesModal.types'
import { isAdminLoggedIn } from '../../shared/functions/Auth/auth.functions'
import localStorage from '../../shared/functions/LocalStorage/localStorage'
import { UserRole } from '../../shared/functions/UserRole/userRoleManagement.types'
import { getUserRole } from '../../shared/functions/UserRole/userRoleManagment.functions'
import { useAppDispatch, useAppSelector } from '../../shared/hooks/redux.hooks'
import { CART_STORAGE_KEY, FLIGHT_ITINERARY_ID_STORAGE_KEY, REDIRECT_TO_CART_KEY } from '../Cart/Cart.dictionary'
import { CartItem } from '../Cart/Cart.types'
import { FlightSearchInitialValues } from '../Travel/components/FlightSearchWidget/FlightSearchWidget.types'
import {
  EXISTING_FLIGHT_ITINERARY_KEY,
  FARE_OFFER_STORAGE_KEY,
  IS_BOOKING_FLIGHT_KEY,
  ITINERARY_KEY,
  REDIRECT_TO_OFFERS_PAGE_TO_ADD_EXISTING_FLIGHT_KEY,
  REDIRECT_TO_TRAVEL_PAGE_TO_ADD_EXISTING_FLIGHT_KEY,
  REDIRECT_TO_TRIP_SUMMARY_KEY,
  SEARCHED_VALUES_STORAGE_KEY,
  SELECTED_OFFERS_STORAGE_KEY,
  TRIP_PREFERENCES_STORAGE_KEY
} from '../TripSummary/TripSummary.dictionary'
import styles from './Login.styles'
import { LoginFormValues } from './Login.types'

const Login: React.FC = () => {
  const dispatch = useAppDispatch()
  const { loading } = useAppSelector((state) => state.login)
  const navigate = router.navigate()
  const [showPassword, setShowPassword] = useState(false)

  const initialValues: LoginFormValues = {
    email: '',
    password: ''
  }

  const validationSchema = Yup.object({
    email: Yup.string().email('Invalid email address').required('Required'),
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters long')
      .matches(/[A-Z]/, 'Password must contain one uppercase letter')
      .matches(/[a-z]/, 'Password must contain one lowercase letter')
      .matches(/[0-9]/, 'Password must contain one number')
      .required('Required')
  })

  const onSubmit = (values: LoginFormValues) => {
    const loginRequest: LoginRequest = {
      email: values.email,
      password: values.password
    }

    const onSuccess = () => {
      const isAdmin = isAdminLoggedIn()
      const userRole = isAdmin ? UserRole.admin : getUserRole()
      const storedCart: CartItem[] | null = localStorage.getItem(CART_STORAGE_KEY)
      const shouldRedirectToCart: boolean | null =
        localStorage.getItem(REDIRECT_TO_CART_KEY) && userRole === UserRole.shopper
      const shouldRedirectToTripSummary =
        localStorage.getItem(REDIRECT_TO_TRIP_SUMMARY_KEY) && userRole === UserRole.traveler
      const shouldRedirectToTravelPageToAddExistingFlight = localStorage.getItem(
        REDIRECT_TO_TRAVEL_PAGE_TO_ADD_EXISTING_FLIGHT_KEY
      )
      const shouldRedirectToOffersPageToAddExistingFlight = localStorage.getItem(
        REDIRECT_TO_OFFERS_PAGE_TO_ADD_EXISTING_FLIGHT_KEY
      )

      if (shouldRedirectToTravelPageToAddExistingFlight) {
        localStorage.removeItem(REDIRECT_TO_TRAVEL_PAGE_TO_ADD_EXISTING_FLIGHT_KEY)
        navigate(routes.home.path)
      } else if (shouldRedirectToOffersPageToAddExistingFlight) {
        localStorage.removeItem(REDIRECT_TO_OFFERS_PAGE_TO_ADD_EXISTING_FLIGHT_KEY)
        navigate(routes.offers.path)
      } else if (shouldRedirectToCart) {
        const flightItineraryId: number | null = localStorage.getItem(FLIGHT_ITINERARY_ID_STORAGE_KEY)

        navigate(routes.cart.path, {
          state: {
            cart: storedCart,
            shouldNavigateToCart: shouldRedirectToCart,
            flightItineraryId: Number(flightItineraryId)
          }
        })

        localStorage.removeItem(REDIRECT_TO_CART_KEY)
        localStorage.removeItem(CART_STORAGE_KEY)
        localStorage.removeItem(FLIGHT_ITINERARY_ID_STORAGE_KEY)
      } else if (shouldRedirectToTripSummary) {
        const fareOffer: FareOffer | null = localStorage.getItem(FARE_OFFER_STORAGE_KEY)
        const selectedOffers: OfferRequest[] | null = localStorage.getItem(SELECTED_OFFERS_STORAGE_KEY)
        const tripPreferences: TripPreferences | null = localStorage.getItem(TRIP_PREFERENCES_STORAGE_KEY)
        const searchedValues: FlightSearchInitialValues | null = localStorage.getItem(SEARCHED_VALUES_STORAGE_KEY)
        const existingFlightItinerary: FlightItinerary | null = localStorage.getItem(EXISTING_FLIGHT_ITINERARY_KEY)
        const itinerary: FlightItinerary | null = localStorage.getItem(ITINERARY_KEY)
        const isBookingFlight: boolean | null = localStorage.getItem(IS_BOOKING_FLIGHT_KEY)

        localStorage.removeItem(REDIRECT_TO_TRIP_SUMMARY_KEY)
        localStorage.removeItem(FARE_OFFER_STORAGE_KEY)
        localStorage.removeItem(SELECTED_OFFERS_STORAGE_KEY)
        localStorage.removeItem(TRIP_PREFERENCES_STORAGE_KEY)
        localStorage.removeItem(SEARCHED_VALUES_STORAGE_KEY)
        localStorage.removeItem(EXISTING_FLIGHT_ITINERARY_KEY)
        localStorage.removeItem(ITINERARY_KEY)
        localStorage.removeItem(IS_BOOKING_FLIGHT_KEY)

        navigate(routes.tripSummary.path, {
          state: {
            fare: fareOffer,
            selectedOffers,
            tripPreferences,
            searchedValues,
            existingFlightItinerary,
            itinerary,
            isBookingFlight
          }
        })
      } else {
        switch (userRole) {
          case UserRole.admin:
            navigate(routes.adminDashboard.path, { replace: true })
            break

          case UserRole.shopper:
            navigate(routes.shop.path, { replace: true })
            break

          default:
            navigate(routes.home.path, { replace: true })
        }
      }
    }

    dispatch(login({ loginRequest, onSuccess }))
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const handleBlur = () => {
    setShowPassword(false)
  }

  const formFields: FormField[] = [
    {
      name: 'email',
      label: 'Email Address',
      type: 'email',
      autoComplete: 'email',
      placeholder: 'john.doe@example.com',
      grid: { xs: 12 },
      required: true,
      fullWidth: true
    },
    {
      name: 'password',
      label: 'Password',
      type: showPassword ? 'text' : 'password',
      autoComplete: 'current-password',
      grid: { xs: 12 },
      required: true,
      fullWidth: true,
      onBlur: handleBlur,
      endAdornment: (
        <IconButton onMouseDown={(e) => e.preventDefault()} onClick={togglePasswordVisibility}>
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      )
    }
  ]

  return (
    <Container component="main" maxWidth="sm" sx={styles.container}>
      <Form
        title="Start Booking Today"
        subtitle="You could earn big on your next trip"
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values: unknown) => onSubmit(values as LoginFormValues)}
        formFields={formFields}
        loading={loading}
        buttonText="Log In"
      />

      <Box sx={styles.alternativeActionsContainer}>
        <Typography sx={styles.loginTextContainer}>
          <Typography sx={styles.loginText}>Don't have an account already?</Typography>

          <Typography sx={styles.loginText}>
            <Link href={routes.signUp.path} ml={0.5} showColor>
              Sign Up
            </Link>
          </Typography>
        </Typography>

        <Typography sx={styles.loginText}>
          <Link href={routes.forgetPassword.path} ml={0.5} showColor>
            Forgot Password?
          </Link>
        </Typography>
      </Box>

      <Box sx={styles.termsContainer}>
        <Divider sx={styles.divider} />

        <Typography sx={styles.termsTextContainer} component="span">
          {`By logging in, you agree to our  \u00A0`}
          <Typography component="span" sx={styles.termsText}>
            <Link href={routes.termsOfServices.path} showColor isUnderlined>
              Terms of Service
            </Link>
          </Typography>
          {`\u00A0and\u00A0`}
          <Typography component="span" sx={styles.termsText}>
            <Link href={routes.privacyPolicy.path} showColor isUnderlined>
              Privacy Policy.
            </Link>
          </Typography>
        </Typography>
      </Box>
    </Container>
  )
}

export default Login
